import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/lib/auth";
import { meApi, type DeliveryInput } from "@/lib/api";
import { STATUS_META, money } from "@/lib/order";
import DeliveryPicker from "@/components/orders/DeliveryPicker";

type Tab = "quotations" | "designs" | "artwork" | "orders" | "details";
const TABS: { key: Tab; label: string; icon: string }[] = [
  { key: "quotations", label: "Quotations", icon: "💬" },
  { key: "designs", label: "Designs", icon: "🎨" },
  { key: "artwork", label: "Artwork", icon: "🖼️" },
  { key: "orders", label: "Orders", icon: "📦" },
  { key: "details", label: "My Details", icon: "👤" },
];

function fmt(d?: string) {
  return d ? d.replace("T", " ").slice(0, 16) : "";
}

export default function AccountPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("quotations");

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-[#0b111c] text-white">
        <div className="mx-auto max-w-6xl px-6 py-12">
          <h1 className="text-4xl font-black">My Account</h1>
          <p className="text-gray-400 mt-1">
            {user?.name || user?.email}
            {user?.role === "superadmin" && (
              <span className="ml-3 rounded-full bg-amber-500/20 text-amber-300 px-3 py-1 text-xs font-bold">
                SUPERADMIN
              </span>
            )}
          </p>

          <div className="mt-8 flex flex-wrap gap-2 border-b border-sky-500/20">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-5 py-3 font-semibold rounded-t-lg transition ${
                  tab === t.key
                    ? "bg-[#11192b] text-sky-400 border-b-2 border-sky-400"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                <span className="mr-2">{t.icon}</span>
                {t.label}
              </button>
            ))}
          </div>

          <div className="mt-8">
            {tab === "quotations" && <QuotationsTab />}
            {tab === "designs" && <DesignsTab />}
            {tab === "artwork" && <ArtworkTab />}
            {tab === "orders" && <OrdersTab />}
            {tab === "details" && <DetailsTab />}
          </div>
        </div>
      </main>
    </>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="rounded-2xl bg-[#11192b] border border-sky-500/10 p-10 text-center text-gray-400">{children}</div>;
}
function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-xl bg-[#11192b] border border-sky-500/10 p-5">{children}</div>;
}

// Quotations = the user's orders that carry a quote (or have progressed past a
// draft). The quotation stays visible here even after it becomes a paid order.
function QuotationsTab() {
  const [rows, setRows] = useState<any[] | null>(null);
  useEffect(() => {
    meApi.orders().then((r) => setRows(r.orders)).catch(() => setRows([]));
  }, []);
  if (!rows) return <Empty>Loading…</Empty>;
  const quotes = rows.filter(
    (o) => o.quote_amount != null || (o.status !== "draft" && o.status !== "cancelled")
  );
  if (!quotes.length)
    return (
      <Empty>
        No quotations yet. Design something in the{" "}
        <Link to="/studio" className="text-sky-400 hover:underline">
          studio
        </Link>{" "}
        and request a quote — it'll appear here.
      </Empty>
    );
  return (
    <div className="grid gap-4">
      {quotes.map((o) => {
        const meta = STATUS_META[o.status] ?? STATUS_META.draft;
        const amount = o.quote_amount != null ? o.quote_amount : o.total;
        const quoted = o.quote_amount != null;
        return (
          <Link key={o.id} to={`/orders/${o.id}`} className="block">
            <Card>
              <div className="flex items-center justify-between gap-3">
                <span className="font-bold">{o.order_number || o.id.slice(0, 8)}</span>
                <div className="flex items-center gap-2">
                  {o.payment_status === "paid" && (
                    <span className="rounded-full bg-emerald-500/20 text-emerald-300 px-3 py-1 text-xs font-bold">
                      PAID
                    </span>
                  )}
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${meta.className}`}>
                    {meta.label}
                  </span>
                </div>
              </div>
              <div className="mt-3 flex items-end justify-between">
                <div>
                  <p className="text-gray-400 text-xs">{quoted ? "Quoted total" : "Estimated total"}</p>
                  <p className="text-xl font-black">{money(amount)}</p>
                </div>
                <span className="text-gray-500 text-sm">{fmt(o.created_at)}</span>
              </div>
              {o.status === "quoted" && (
                <p className="mt-2 text-xs text-amber-300">Awaiting your acceptance — tap to review &amp; accept.</p>
              )}
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

function DesignsTab() {
  const [rows, setRows] = useState<any[] | null>(null);
  async function load() {
    try {
      setRows((await meApi.designs()).designs);
    } catch {
      setRows([]);
    }
  }
  useEffect(() => {
    load();
  }, []);
  if (!rows) return <Empty>Loading…</Empty>;
  if (!rows.length)
    return (
      <Empty>
        No saved designs yet. Open the{" "}
        <Link to="/studio" className="text-sky-400 hover:underline">
          Design Studio
        </Link>{" "}
        and hit Save.
      </Empty>
    );
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((d) => (
        <Card key={d.id}>
          <Link to={`/studio?design=${d.id}`} className="block group" title="Open in studio">
            <div className="aspect-square rounded-lg bg-[#0b111c] overflow-hidden flex items-center justify-center mb-3 ring-1 ring-transparent group-hover:ring-sky-400/50 transition">
              {d.thumbnail ? (
                <img src={d.thumbnail} alt={d.name} className="max-h-full max-w-full object-contain" />
              ) : (
                <span className="text-gray-600 text-5xl">🎨</span>
              )}
            </div>
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold">{d.name}</p>
              <p className="text-xs text-gray-500">
                {d.kind === "gangsheet" ? "Gang Sheet" : d.product || "—"} · {fmt(d.updated_at)}
              </p>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Link to={`/studio?design=${d.id}`} className="text-sky-400 hover:underline">
                Open
              </Link>
              <button
                onClick={async () => {
                  await meApi.deleteDesign(d.id);
                  load();
                }}
                className="text-gray-500 hover:text-red-400"
              >
                Delete
              </button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function ArtworkTab() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [err, setErr] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);
  async function load() {
    try {
      setRows((await meApi.artwork()).artwork);
    } catch {
      setRows([]);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    setErr("");
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 700_000) {
      setErr("File too large — max ~700KB for now (R2 upload coming soon).");
      return;
    }
    const data: string = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(file);
    });
    try {
      await meApi.uploadArtwork({ filename: file.name, content_type: file.type, data });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e: any) {
      setErr(e.message || "Upload failed");
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center gap-4">
        <label className="cursor-pointer rounded-xl bg-sky-500 px-5 py-3 font-bold text-white hover:bg-sky-400 transition">
          Upload artwork
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />
        </label>
        {err && <span className="text-amber-400 text-sm">{err}</span>}
      </div>
      {!rows ? (
        <Empty>Loading…</Empty>
      ) : !rows.length ? (
        <Empty>No artwork uploaded yet.</Empty>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {rows.map((a) => (
            <Card key={a.id}>
              <div className="aspect-square rounded-lg bg-[#0b111c] overflow-hidden flex items-center justify-center mb-3">
                {a.content_type?.startsWith("image/") && a.data ? (
                  <img src={a.data} alt={a.filename} className="max-h-full max-w-full object-contain" />
                ) : (
                  <span className="text-gray-600 text-4xl">🖼️</span>
                )}
              </div>
              <p className="font-semibold truncate">{a.filename}</p>
              <p className="text-xs text-gray-500">
                {Math.round((a.size || 0) / 1024)} KB · {fmt(a.created_at)}
              </p>
              <button
                onClick={async () => {
                  await meApi.deleteArtwork(a.id);
                  load();
                }}
                className="mt-2 text-gray-500 hover:text-red-400 text-sm"
              >
                Delete
              </button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DetailsTab() {
  const [profile, setProfile] = useState<any | null>(null);
  const [name, setName] = useState("");
  const [delivery, setDelivery] = useState<DeliveryInput>({ method: "courier" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    meApi
      .profile()
      .then((r) => {
        setProfile(r.profile);
        setName(r.profile.name || "");
        setDelivery({
          method: r.profile.delivery_method || "courier",
          phone: r.profile.phone || "",
          address: r.profile.address || "",
          locker_code: r.profile.locker_code || "",
          locker_name: r.profile.locker_name || "",
        });
      })
      .catch(() => setProfile({}));
  }, []);

  if (!profile) return <Empty>Loading…</Empty>;

  async function save() {
    setBusy(true);
    setMsg("");
    try {
      await meApi.updateProfile({
        name,
        delivery_method: delivery.method,
        phone: delivery.phone,
        address: delivery.address,
        locker_code: delivery.locker_code,
        locker_name: delivery.locker_name,
      });
      setMsg("Saved ✓ — we'll use these details for your next order.");
    } catch (e: any) {
      setMsg(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <Card>
        <label className="block text-sm text-gray-400">
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-lg bg-[#0b111c] border border-sky-500/20 px-4 py-3 text-white"
          />
        </label>
      </Card>
      <Card>
        <h3 className="font-bold mb-1">Default delivery</h3>
        <p className="text-gray-400 text-sm mb-4">
          Saved here and pre-filled on every order — you can still change it per order.
        </p>
        <DeliveryPicker value={delivery} onChange={setDelivery} />
      </Card>
      <button
        onClick={save}
        disabled={busy}
        className="rounded-xl bg-sky-500 px-6 py-3 font-bold text-white hover:bg-sky-400 disabled:opacity-60"
      >
        {busy ? "Saving…" : "Save my details"}
      </button>
      {msg && <p className="text-sky-300 text-sm">{msg}</p>}
    </div>
  );
}

function OrdersTab() {
  const [rows, setRows] = useState<any[] | null>(null);
  useEffect(() => {
    meApi.orders().then((r) => setRows(r.orders)).catch(() => setRows([]));
  }, []);
  if (!rows) return <Empty>Loading…</Empty>;
  if (!rows.length)
    return (
      <Empty>
        No orders yet. Design something in the{" "}
        <Link to="/studio" className="text-sky-400 hover:underline">
          studio
        </Link>{" "}
        and request a quote.
      </Empty>
    );
  return (
    <div className="grid gap-4">
      {rows.map((o) => {
        const meta = STATUS_META[o.status] ?? STATUS_META.draft;
        return (
          <Link key={o.id} to={`/orders/${o.id}`} className="block">
            <Card>
              <div className="flex justify-between items-center gap-3">
                <span className="font-bold">{o.order_number || o.id.slice(0, 8)}</span>
                <div className="flex items-center gap-2">
                  {o.payment_status === "paid" && (
                    <span className="rounded-full bg-emerald-500/20 text-emerald-300 px-3 py-1 text-xs font-bold">
                      PAID
                    </span>
                  )}
                  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${meta.className}`}>
                    {meta.label}
                  </span>
                </div>
              </div>
              <div className="mt-3 flex justify-between text-sm text-gray-400">
                <span>
                  {o.item_count} item{o.item_count === 1 ? "" : "s"}
                  {o.delivery_method ? ` · ${o.delivery_method === "pudo" ? "Pudo locker" : "Courier"}` : ""}
                </span>
                <span>{o.quote_amount != null ? money(o.quote_amount) : fmt(o.created_at)}</span>
              </div>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}
